import Input from 'components/Input';
import Select from 'components/Select';
import React, { useState } from 'react';
import { Button, Modal } from "react-bootstrap";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createPaySchedule } from 'services/paySchedules';

const toISO = (dateStr) => dateStr ? new Date(dateStr).toISOString() : undefined;

const FREQUENCY_OPTIONS = [
    { value: 'Weekly', label: 'Weekly' },
    { value: 'BiWeekly', label: 'Bi-Weekly' },
    { value: 'SemiMonthly', label: 'Semi-Monthly' },
    { value: 'Monthly', label: 'Monthly' },
];

const initialForm = { name: '', frequency: '', firstPeriodEnd: '', firstPayday: '', secondPayday: '' };

function AddPayScheduleModal({ open, onClose }) {
    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: createPaySchedule,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['pay-schedules'] });
            setForm(initialForm);
            setErrors({});
            onClose();
        },
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        const newErrors = {};
        if (!form.name.trim()) newErrors.name = 'Name is required.';
        if (!form.frequency) newErrors.frequency = 'Frequency is required.';
        if (!form.firstPeriodEnd) newErrors.firstPeriodEnd = 'First period end is required.';
        if (!form.firstPayday) newErrors.firstPayday = 'First payday is required.';
        return newErrors;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const newErrors = validate();
        if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }
        const payload = {
            name: form.name,
            frequency: form.frequency,
            firstPeriodEnd: toISO(form.firstPeriodEnd),
            firstPayday: toISO(form.firstPayday),
            active: true,
        };
        if (form.secondPayday) payload.secondPayday = toISO(form.secondPayday);
        mutation.mutate(payload);
    };

    const handleClose = () => { setForm(initialForm); setErrors({}); mutation.reset(); onClose(); };

    return (
        <Modal className='add-workplace-modal' show={open} onHide={handleClose} centered>
            <div className="modal-header border-0 pb-0 d-flex justify-content-between align-items-center">
                <h4 className="sub-title mb-0">Add Pay Schedule</h4>
                <button type="button" className="close bg-transparent border-0" onClick={handleClose}>
                    <span aria-hidden="true">&times;</span>
                </button>
            </div>
            <Modal.Body className='pt-0'>
                <form onSubmit={handleSubmit}>
                    <div className='border-bottom mb-4 pb-4'>
                        <h2 className='sub-title workplace-information-title mb-0'>General Information</h2>
                        <div className='row mt-4'>
                            <div className='col-lg-6'>
                                <label>Name*</label>
                                <Input variant='flat' fullWidth placeholder='Enter name' name='name' value={form.name} onChange={handleChange} error={!!errors.name} errorText={errors.name} />
                            </div>
                            <div className='col-lg-6'>
                                <label>Frequency*</label>
                                <Select fullWidth placeholder='Select frequency' options={FREQUENCY_OPTIONS} name='frequency' value={form.frequency} onChange={handleChange} error={!!errors.frequency} errorText={errors.frequency} />
                            </div>
                        </div>
                    </div>
                    <div>
                        <h2 className='sub-title workplace-information-title mb-0'>Schedule Information</h2>
                        <div className='row mt-4'>
                            <div className='col-lg-6'>
                                <label className='input-label'>First Period End*</label>
                                <input
                                    type='date'
                                    name='firstPeriodEnd'
                                    value={form.firstPeriodEnd}
                                    onChange={handleChange}
                                    style={{ display:'block', width:'100%', padding:'8px 10px', border: errors.firstPeriodEnd ? '1px solid #dc3545' : '1px solid #dee2e6', borderRadius:8, fontSize:14, background:'#fff', cursor:'pointer' }}
                                />
                                {errors.firstPeriodEnd && <p className='error-text'>{errors.firstPeriodEnd}</p>}
                            </div>
                            <div className='col-lg-6'>
                                <label className='input-label'>First Pay Day*</label>
                                <input
                                    type='date'
                                    name='firstPayday'
                                    value={form.firstPayday}
                                    onChange={handleChange}
                                    style={{ display:'block', width:'100%', padding:'8px 10px', border: errors.firstPayday ? '1px solid #dc3545' : '1px solid #dee2e6', borderRadius:8, fontSize:14, background:'#fff', cursor:'pointer' }}
                                />
                                {errors.firstPayday && <p className='error-text'>{errors.firstPayday}</p>}
                            </div>
                            <div className='col-lg-6 mt-3'>
                                <label className='input-label'>Second Pay Day</label>
                                <input
                                    type='date'
                                    name='secondPayday'
                                    value={form.secondPayday}
                                    onChange={handleChange}
                                    style={{ display:'block', width:'100%', padding:'8px 10px', border:'1px solid #dee2e6', borderRadius:8, fontSize:14, background:'#fff', cursor:'pointer' }}
                                />
                            </div>
                        </div>
                    </div>
                    {mutation.isError && (
                        <p className="text-danger mt-3">
                            {mutation.error?.response?.data?.message || mutation.error?.response?.data?.title || 'Failed to add pay schedule. Please try again.'}
                        </p>
                    )}
                    <Modal.Footer className='border-0 justify-content-start px-0 mb-5'>
                        <Button type='submit' className='button-orange' disabled={mutation.isPending}>
                            {mutation.isPending ? 'Saving...' : 'Add Pay Schedule'}
                        </Button>
                    </Modal.Footer>
                </form>
            </Modal.Body>
        </Modal>
    );
}

export default AddPayScheduleModal;
